'use client';

import { c as _c } from "react/compiler-runtime";
import type { Post } from '@shared/all/domains/posts';
import AuthWidgetContext from '@shared/utils/context/AuthWidgetContext';
import { copyToClipboard } from '@shared/utils/helpers/clipboard';
import toast from '@shared/utils/helpers/toast';
import useUser from '@shared/utils/hooks/useUser';
import { Code, Copy, Repeat2, Share2 } from 'lucide-react';
import type React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { animDelay } from '../../utils/animDelay';
import EmbedPostModal from './EmbedPostModal';
import NewRepostModal from './NewRepostModal';
type Props = {
  post: Post;
  nsfw: boolean;
  politics: boolean;
  minified?: boolean;
};
const PostShareButton = props => {
  const $ = _c(43);
  const {
    currentUser
  } = useUser();
  const {
    openAuthContinueModal
  } = useContext(AuthWidgetContext);
  const [shareOpen, setShareOpen] = useState(false);
  const [repostOpen, setRepostOpen] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const shareButtonRef = useRef(null);
  const sharePopoverRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      bottom: 0,
      left: 0
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [sharePosition, setSharePosition] = useState(t0);
  let t1;
  let t2;
  if ($[1] !== shareOpen) {
    t1 = () => {
      if (!shareOpen) {
        return;
      }
      const updatePosition = () => {
        if (!shareButtonRef.current) {
          return;
        }
        const rect = shareButtonRef.current.getBoundingClientRect();
        setSharePosition({
          bottom: window.innerHeight - rect.top + 8,
          left: rect.left + rect.width / 2 - 80
        });
      };
      updatePosition();
      const handleClick = e => {
        if (!sharePopoverRef.current?.contains(e.target as Node) && !shareButtonRef.current?.contains(e.target as Node)) {
          setShareOpen(false);
        }
      };
      document.addEventListener("click", handleClick);
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        document.removeEventListener("click", handleClick);
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    };
    t2 = [shareOpen];
    $[1] = shareOpen;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== linkCopied || $[5] !== props.post) {
    t3 = async () => {
      const url = `${process.env.NEXT_PUBLIC_SITE_URL}/post/${props.post.id}`;
      const ok = await copyToClipboard(url);
      if (!ok) {
        toast("Failed to copy link");
        return;
      }
      toast("Link copied to clipboard!");
      if (linkCopied) {
        return;
      }
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    };
    $[4] = linkCopied;
    $[5] = props.post;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleCopy = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Repeat2 size={18} />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== currentUser || $[9] !== openAuthContinueModal) {
    t5 = {
      label: "Repost",
      icon: t4,
      onClick: () => {
        if (!currentUser.isAuthenticated) {
          return openAuthContinueModal();
        }
        setShareOpen(false);
        setRepostOpen(true);
      }
    };
    $[8] = currentUser;
    $[9] = openAuthContinueModal;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      label: "Embed",
      icon: <Code size={18} />,
      onClick: () => {
        setShareOpen(false);
        setEmbedOpen(true);
      }
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = linkCopied ? "Link Copied!" : "Copy Link";
  const t8 = `transition-colors duration-300 ${linkCopied ? "text-green-500" : ""}`;
  let t9;
  if ($[12] !== t8) {
    t9 = <Copy size={18} className={t8} />;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== handleCopy) {
    t10 = () => {
      handleCopy();
      setShareOpen(false);
    };
    $[14] = handleCopy;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t7 || $[18] !== t9) {
    t11 = {
      label: t7,
      icon: t9,
      onClick: t10
    };
    $[16] = t10;
    $[17] = t7;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t5) {
    t12 = [t5, t6, t11];
    $[20] = t11;
    $[21] = t5;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const shareOptions = t12;
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = e_0 => {
      e_0.stopPropagation();
      setShareOpen(_temp);
    };
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = props.minified ? 14 : 18;
  let t15;
  if ($[24] !== t14) {
    t15 = <button ref={shareButtonRef} title="Share" className="flex items-center justify-center rounded-md px-2 py-1 transition-transform duration-200 hover:scale-[1.05] hover:bg-background-light active:scale-[0.95]" onClick={t13}><Share2 size={t14} className="text-textSecondary" /></button>;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== shareOpen || $[27] !== shareOptions || $[28] !== sharePosition) {
    t16 = shareOpen && typeof document !== "undefined" && createPortal(<div ref={sharePopoverRef} className="z-popover" style={{
      position: "fixed",
      bottom: sharePosition.bottom,
      left: sharePosition.left
    }} onClick={_temp2}><div className="anim-enter w-40 space-y-1 rounded-md border border-ds-border-standard bg-background-primary p-2 shadow-lg [--enter-duration:150ms] [--enter-y:6px]">{shareOptions.map(_temp3)}</div></div>, document.body);
    $[26] = shareOpen;
    $[27] = shareOptions;
    $[28] = sharePosition;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== props.nsfw || $[31] !== props.politics || $[32] !== props.post || $[33] !== repostOpen) {
    t17 = repostOpen && <NewRepostModal closeHandler={() => setRepostOpen(false)} childPost={props.post} isChildPostPolitics={props.politics} isChildPostNsfw={props.nsfw} />;
    $[30] = props.nsfw;
    $[31] = props.politics;
    $[32] = props.post;
    $[33] = repostOpen;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== embedOpen || $[36] !== props.post) {
    t18 = embedOpen && <EmbedPostModal postId={props.post.id} closeHandler={() => setEmbedOpen(false)} />;
    $[35] = embedOpen;
    $[36] = props.post;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t15 || $[39] !== t16 || $[40] !== t17 || $[41] !== t18) {
    t19 = <>{t15}{t16}{t17}{t18}</>;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  return t19;
};
export default PostShareButton;
function _temp(x) {
  return !x;
}
function _temp2(e_1) {
  return e_1.stopPropagation();
}
function _temp3(opt, index) {
  return <button key={opt.label} style={animDelay(index * 30)} className="anim-enter flex w-full items-center gap-2 rounded-md p-2 text-left text-sm text-textSecondary/80 transition-colors duration-200 [--enter-duration:150ms] [--enter-x:-6px] hover:bg-background-light" onClick={e_2 => {
    e_2.stopPropagation();
    opt.onClick();
  }}><span className="shrink-0">{opt.icon}</span><span>{opt.label}</span></button>;
}